import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { GlobalFooter } from '@nexus/fff-global-footer';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import env from 'utils/env';
import { makeSelectProducts } from 'selectors/productsSelectors';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';
import { selectIsCampaignOpen } from 'services/reducers/campaignReducer';
import { requestBoostData } from 'services/actions/campaignActions';

import { track } from 'utils/segment';

import withViewSize from 'HOC/withViewSize/withViewSize';
import GlobalNav from 'shared-components/GlobalNav/GlobalNav';
import BoostProduct from '../BoostProduct/BoostProduct';
import ErrorPage from '../../ErrorPages/ErrorPage';
import LearnMoreModal from '../LearnMoreModal/LearnMoreModal';
import RemovalWarningPopup from '../RemovalWarningPopup/RemovalWarningPopup';
import ProgressNavigationBar from '../ProgressNavigationBar/ProgressNavigationBar';
import BoostPageHeader from './BoostPageHeader/BoostPageHeader';

import { getBoxState } from 'services/api/box';

import styles from './BoostPage.module.css';

const selectProducts = makeSelectProducts();

const SKIPPED_INTRO_BOX_STATE = 'SKIPPED_INTRO';

function BoostPage(props) {
  const {
    requestBoostData,
    fetchingCampaign,
    fetchingProducts,
    isCampaignOpen,
    products,
    productsRequestFailed,
    customizeLastQuestionId,
    campaignError,
    trackingData,
    viewSize,
    isFutureOfCustomize,
    flags,
  } = props;
  const { replenishment } = flags;
  const [learnMoreVariantId, setLearnMoreVariantId] = useState(null);
  const openLearnMoreModal = (id) => setLearnMoreVariantId(id);
  const closeLearnMoreModal = () => setLearnMoreVariantId(null);

  const [variantToRemove, setVariantToRemove] = useState(null);
  const showRemovalWarning = (id) => setVariantToRemove(id);
  const hideRemovalWarning = () => setVariantToRemove(null);
  const variantToRemoveParentProduct = products.find((product) =>
    product.variantIds.includes(variantToRemove)
  );
  const variantToRemoveParentProductId =
    variantToRemoveParentProduct && variantToRemoveParentProduct.id;

  useEffect(() => {
    const fetchBoxState = async () => {
      const { boxState } = await getBoxState();
      if (boxState === SKIPPED_INTRO_BOX_STATE) {
        window.location.replace(`https://${env.hostname}/customize`);
      } else {
        requestBoostData();
      }
    };
    fetchBoxState();
  }, []);

  // redirect to customize because of the window resterction or campaign closed
  if (!fetchingCampaign) {
    if (!isCampaignOpen) {
      return window.location.replace(`https://${env.hostname}/customize`);
    }
    // redirect to mybox page if no products because of shipping restrictions
    if (!fetchingProducts && products.length === 0) {
      return window.location.replace(
        `https://${env.hostname}/customize/confirmation`
      );
    }
  }
  if (campaignError || productsRequestFailed) return <ErrorPage />;

  function handleTrackClick(experience, elementName) {
    const eventName = 'Clicked Element';
    track(eventName, {
      ...trackingData,
      experience,
      element_name: elementName,
    });
  }

  const backLink = replenishment
    ? `https://${env.hostname}/refills/`
    : `${env.customizePath}/${
        customizeLastQuestionId ? `?q=${customizeLastQuestionId}` : ''
      }`;

  return (
    <>
      <div className={viewSize}>
        <GlobalNav hideCart />
        <div className={styles.boostPage}>
          {isFutureOfCustomize && (
            <ProgressNavigationBar
              viewSize={viewSize}
              replenishment={replenishment}
            />
          )}
          <BoostPageHeader />
          <div className={`${styles.container}`}>
            <section className={styles.products}>
              {products.map((product) => {
                const { id, brandName, name, variants, description } = product;
                const productDisplay = brandName
                  ? `${brandName} ${name}`
                  : name;
                return (
                  <BoostProduct
                    key={id}
                    name={productDisplay}
                    variants={variants}
                    description={description}
                    openLearnMoreModal={openLearnMoreModal}
                    showRemovalWarning={() =>
                      showRemovalWarning(variants[0].id)
                    }
                  />
                );
              })}
            </section>
            <div className={`${styles.navButtonsContainer}`}>
              <a
                className={`${styles.backButton}`}
                href={`${backLink}`}
                onClick={() => handleTrackClick('Boost Navigation', 'BACK')}
              >
                BACK
              </a>
              <a
                className={`${styles.finishButton}`}
                href={`${env.customizePath}/confirmation`}
                onClick={() => handleTrackClick('Boost Navigation', 'FINISH')}
              >
                FINISH
              </a>
            </div>
          </div>
        </div>
        <GlobalFooter />
      </div>
      {learnMoreVariantId && (
        <LearnMoreModal
          variantId={learnMoreVariantId}
          closeModal={closeLearnMoreModal}
        />
      )}
      <RemovalWarningPopup
        hideRemovalWarning={hideRemovalWarning}
        variantToRemove={variantToRemove}
        variantToRemoveParentProductId={variantToRemoveParentProductId}
      />
    </>
  );
}

function futureOfCustomize(state): boolean {
  if (!state.customizeState.experimentInfo) {
    return true;
  }
  return (
    !state.customizeState.experimentInfo.qualified ||
    state.customizeState.experimentInfo.variation !== 'CHALLENGER'
  );
}

function mapStateToProps(state, props) {
  return {
    fetchingCampaign: state.campaignState.fetchingCampaign,
    fetchingProducts: state.productsState.fetchingProducts,
    isCampaignOpen: selectIsCampaignOpen(state),
    products: selectProducts(state, props),
    productsRequestFailed: state.productsState.productsRequestFailed,
    customizeLastQuestionId: state.customizeState.lastQuestionId,
    isFutureOfCustomize: futureOfCustomize(state),
    campaignError: state.campaignState.campaignError,
    trackingData: selectPageTrackingFullData(state),
    viewSize: state.viewState.viewSize,
  };
}

const mapDispatchToProps = {
  requestBoostData,
};

export default compose(
  withViewSize,
  withLDConsumer(),
  connect(mapStateToProps, mapDispatchToProps)
)(BoostPage);
